import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, BookOpen, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { cart, loading, updateCartItem, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [shippingSettings, setShippingSettings] = useState({
    shipping_rate: 3.99,
    free_shipping_threshold: 50.00,
    shipping_enabled: true
  });

  useEffect(() => {
    fetchShippingSettings();
  }, []);

  const fetchShippingSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setShippingSettings({
        shipping_rate: response.data.shipping_rate || 3.99,
        free_shipping_threshold: response.data.free_shipping_threshold || 50.00,
        shipping_enabled: response.data.shipping_enabled !== false
      });
    } catch (error) {
      console.error('Failed to fetch shipping settings:', error);
    }
  };

  // Detect digital-only cart
  const isDigitalOnly = cart.items.length > 0 && cart.items.every(item => item.is_ebook || item.is_digital);

  const getShippingCost = () => {
    if (isDigitalOnly) return 0;
    if (!shippingSettings.shipping_enabled) return 0;
    if (cart.total >= shippingSettings.free_shipping_threshold) return 0;
    return shippingSettings.shipping_rate;
  };

  const shippingCost = getShippingCost();
  const orderTotal = cart.total + shippingCost;
  const amountToFreeShipping = shippingSettings.free_shipping_threshold - cart.total;

  const handleUpdateQuantity = async (productId, newQuantity) => {
    const success = await updateCartItem(productId, newQuantity);
    if (!success) {
      toast.error('Failed to update cart');
    }
  };

  const handleRemoveItem = async (productId, productName) => {
    const success = await removeFromCart(productId);
    if (success) {
      toast.success(`${productName} removed from cart`);
    } else {
      toast.error('Failed to remove item');
    }
  };

  const handleClearCart = async () => {
    const success = await clearCart();
    if (success) {
      toast.success('Cart cleared');
    } else {
      toast.error('Failed to clear cart');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-32" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="empty-cart">
        <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h1 className="font-serif text-2xl font-bold text-foreground mb-2">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-6">Looks like you haven&apos;t added anything to your cart yet.</p>
        <Link to="/products">
          <Button className="btn-primary" data-testid="continue-shopping">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-testid="cart-page">
      <div className="flex items-center justify-between mb-4 sm:mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">Shopping Cart</h1>
        <Button variant="ghost" onClick={handleClearCart} className="text-destructive hover:text-destructive text-sm" data-testid="clear-cart-btn">
          <Trash2 className="h-4 w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Clear Cart</span>
          <span className="sm:hidden">Clear</span>
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {cart.items.map((item) => (
            <Card key={item.product_id} className="overflow-hidden" data-testid={`cart-item-${item.product_id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex gap-3 sm:gap-4">
                  <Link to={item.is_ebook ? `/ebooks/${item.product_id}` : `/product/${item.product_id}`} className="shrink-0">
                    {item.is_ebook && !item.image_url ? (
                      <div className="w-16 h-16 sm:w-24 sm:h-24 bg-muted rounded-md flex items-center justify-center">
                        <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                      </div>
                    ) : (
                      <img
                        src={item.image_url || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
                        alt={item.name}
                        className="w-16 h-16 sm:w-24 sm:h-24 object-cover rounded-md"
                      />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={item.is_ebook ? `/ebooks/${item.product_id}` : `/product/${item.product_id}`}>
                      <h3 className="font-serif font-semibold text-foreground hover:text-primary transition-colors line-clamp-1 text-sm sm:text-base">
                        {item.name}
                      </h3>
                    </Link>
                    {item.is_ebook && (
                      <span className="text-[10px] sm:text-xs text-muted-foreground">Digital Download {item.file_format ? `(${item.file_format.toUpperCase()})` : ''}</span>
                    )}
                    <p className="price-tag text-primary font-bold mt-0.5 sm:mt-1 text-sm sm:text-base">${item.price.toFixed(2)}</p>
                    
                    <div className="flex items-center justify-between mt-2 sm:mt-3">
                      {item.is_ebook ? (
                        <span className="text-[10px] sm:text-xs text-muted-foreground">Qty: 1 (digital)</span>
                      ) : (
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 sm:h-8 sm:w-8"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 sm:w-8 text-center font-medium text-sm">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 sm:h-8 sm:w-8"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive h-7 w-7 sm:h-8 sm:w-8 p-0"
                        onClick={() => handleRemoveItem(item.product_id, item.name)}
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="price-tag font-bold text-lg">${item.subtotal.toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="font-serif">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal ({cart.items.length} items)</span>
                <span>${cart.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                {isDigitalOnly ? (
                  <span className="text-green-600 font-medium">N/A (Digital)</span>
                ) : shippingCost === 0 ? (
                  <span className="text-green-600 font-medium">Free</span>
                ) : (
                  <span>${shippingCost.toFixed(2)}</span>
                )}
              </div>
              
              {/* Free shipping progress - only for physical items */}
              {!isDigitalOnly && shippingSettings.shipping_enabled && amountToFreeShipping > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <Truck className="h-4 w-4" />
                    <span>Add <strong>${amountToFreeShipping.toFixed(2)}</strong> more for free shipping!</span>
                  </div>
                  <div className="mt-2 h-2 bg-amber-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((cart.total / shippingSettings.free_shipping_threshold) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Digital download notice */}
              {isDigitalOnly && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-primary text-sm">
                    <Download className="h-4 w-4" />
                    <span>Digital order — <strong>no shipping needed</strong></span>
                  </div>
                </div>
              )}
              
              {!isDigitalOnly && shippingCost === 0 && cart.total > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <Truck className="h-4 w-4" />
                    <span>You qualify for <strong>free shipping!</strong></span>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="price-tag">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
              <Button
                className="w-full btn-accent"
                size="lg"
                onClick={() => navigate('/checkout')}
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Link to="/products" className="block">
                <Button variant="outline" className="w-full">
                  Continue Shopping
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Cart;
